import {
  Component, EventEmitter, HostBinding, Input, Output, OnDestroy, OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AuthService } from '@app/auth/auth.service';
import { CurrentUserInfo } from '@shared/meta-data/user.meta';
import { MerchantNotificationsService, MerchantNotification } from '@services/merchant-notifications/merchant-notifications.service';
import { AppBanner } from '@shared/meta-data/app-banner.meta';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.css'],
})
export class NavBarComponent implements OnInit, OnDestroy {
  FEATURE_FLAG = environment.FEATURE_FLAG;

  @Output() toggleSidebar = new EventEmitter<void>();
  @Output() navBarToggle = new EventEmitter<void>();
  @Output() logOut = new EventEmitter<void>();
  @Input() user: CurrentUserInfo = {};
  @Input() banner?: AppBanner;
  /** When true, shows a link to open the separate Admin SPA (Cognito admin users). */
  @Input() isCognitoAdmin = false;
  /** When true, shows a link to open the AutoFiler SPA (admins + dedicated autofiling operators). */
  @Input() showAutofilerConsole = false;

  @HostBinding('class') classes = 'd-flex justify-content-between';

  notifications: MerchantNotification[] = [];

  private readonly destroyed = new Subject<void>();

  constructor(
    translate: TranslateService,
    readonly merchantNotifications: MerchantNotificationsService,
    private readonly authService: AuthService,
    private readonly router: Router,
  ) {
    translate.setDefaultLang('en');
  }

  ngOnInit(): void {
    this.merchantNotifications.notifications$
      .pipe(takeUntil(this.destroyed))
      .subscribe((rows) => {
        this.notifications = rows;
      });
    void this.merchantNotifications.refresh().subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  onNotificationClick(notification: MerchantNotification): void {
    void this.merchantNotifications.markRead([String(notification.notification_id)]).subscribe(() => {
      if (notification.link_path) {
        void this.router.navigateByUrl(notification.link_path);
      }
    });
  }

  getUnreadNotifications(): Array<MerchantNotification> {
    return this.notifications.filter((n) => !n.is_read);
  }

  /**
   * Opens the operator Admin SPA and completes the postMessage auth handoff from the merchant window.
   */
  openAdminConsole(): void {
    this.authService.openAdminConsoleWithAuthSync();
  }

  /**
   * Opens the AutoFiler SPA and completes the postMessage auth handoff from the merchant window.
   */
  openAutofilerConsole(): void {
    this.authService.openAutofilerConsoleWithAuthSync();
  }
}
